import { Controller } from "@hotwired/stimulus"

// Collapsed-by-default related services picker with search + chips.
export default class extends Controller {
  static targets = ["primary", "toggle", "panel", "search", "results", "chips", "inputs", "option", "limitNote", "emptyHint"]
  static values = {
    max: { type: Number, default: 5 },
    inputName: { type: String, default: "related_service_ids[]" },
  }

  connect() {
    this.selected = new Map()
    this.optionTargets.forEach((option) => {
      if (option.dataset.selected === "true") {
        this.selected.set(String(option.dataset.id), option.dataset.name)
      }
    })

    if (this.selected.size > 0) {
      this.open()
    } else {
      this.close()
    }

    this.render()
    this.filter()
  }

  toggle(event) {
    event.preventDefault()
    if (this.panelTarget.hidden) {
      this.open()
      this.searchTarget.focus()
    } else if (this.selected.size === 0) {
      this.close()
    } else {
      this.close()
    }
  }

  open() {
    this.panelTarget.hidden = false
    this.toggleTarget.textContent = "Hide related services"
  }

  close() {
    this.panelTarget.hidden = true
    this.toggleTarget.textContent = "+ Add related services"
    this.searchTarget.value = ""
    this.filter()
  }

  primaryChanged() {
    const primaryId = this.primaryId()
    if (primaryId && this.selected.has(primaryId)) {
      this.selected.delete(primaryId)
    }
    this.render()
    this.filter()
  }

  select(event) {
    event.preventDefault()
    const button = event.currentTarget
    const id = String(button.dataset.id)
    const name = button.dataset.name

    if (!id || !name) return
    if (id === this.primaryId()) return
    if (this.selected.has(id)) return
    if (this.selected.size >= this.maxValue) return

    this.selected.set(id, name)
    this.searchTarget.value = ""
    this.render()
    this.filter()
    this.searchTarget.focus()
  }

  remove(event) {
    event.preventDefault()
    const id = String(event.currentTarget.dataset.id)
    this.selected.delete(id)
    this.render()
    this.filter()
    if (this.selected.size === 0 && !this.panelTarget.hidden) {
      this.searchTarget.focus()
    }
  }

  onSearch() {
    this.filter()
  }

  primaryId() {
    if (!this.hasPrimaryTarget) return ""
    return String(this.primaryTarget.value || "")
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    const primaryId = this.primaryId()
    const atLimit = this.selected.size >= this.maxValue
    const hasQuery = query.length > 0
    let visibleCount = 0

    this.optionTargets.forEach((option) => {
      const id = String(option.dataset.id)
      const name = (option.dataset.name || "").toLowerCase()
      const isPrimary = primaryId !== "" && id === primaryId
      const alreadySelected = this.selected.has(id)
      const matches = hasQuery && name.includes(query)
      const show = !isPrimary && !alreadySelected && matches && !atLimit

      option.hidden = !show
      if (show) visibleCount += 1
    })

    this.resultsTarget.hidden = atLimit || !hasQuery || visibleCount === 0
    if (this.hasLimitNoteTarget) {
      this.limitNoteTarget.hidden = !atLimit
    }
    if (this.hasEmptyHintTarget) {
      this.emptyHintTarget.hidden = atLimit || hasQuery
    }
  }

  render() {
    this.chipsTarget.innerHTML = ""
    this.inputsTarget.innerHTML = ""

    this.selected.forEach((name, id) => {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
      chip.innerHTML = `<span></span><button type="button" class="text-slate-500 hover:text-slate-800" aria-label="Remove">&times;</button>`
      chip.querySelector("span").textContent = name
      const button = chip.querySelector("button")
      button.dataset.id = id
      button.dataset.action = "related-services-picker#remove"
      this.chipsTarget.appendChild(chip)

      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.inputNameValue
      input.value = id
      this.inputsTarget.appendChild(input)
    })

    this.filter()
  }
}
